import React, { useMemo, useState } from 'react';
import { SafeAreaView, View, Text, Pressable, Platform, ScrollView, Modal, TouchableOpacity } from 'react-native';
import Svg, { Line } from 'react-native-svg';
import { MaterialIcons, Ionicons } from '@expo/vector-icons';
import DateRangePicker, { DateRange } from '../components/DateRangePicker.native';
import { usePartnerInsightsData } from '../hooks/usePartnerInsightsData';
//@ts-ignore  
import DailyKwhBarChart from '../components/graphs/DailyKwhBarChart';
//@ts-ignore
import AccumulatedEarningsLineChart from '../components/graphs/AccumulatedEarningsLineChart';
//@ts-ignore
import ActiveInactivePieChart from '../components/graphs/ActiveInactivePieChart';
import { Dropdown, MultiSelect } from 'react-native-element-dropdown';
import { dropdownStyles } from '../styles/DropdownStyles';
import { useRoute } from '@react-navigation/native';

type RouteParams = {
  preselectedChargerIds?: string[];
};

const PartnerInsights: React.FC = () => {
  const route = useRoute<any>();
  const incoming: RouteParams | undefined = route?.params;
  const [pickerVisible, setPickerVisible] = useState(false);
  const [lifetimeEarningsTooltipVisible, setLifetimeEarningsTooltipVisible] = useState(false);

  const {
    data,
    chargers,
    loading,
    selectedStartDate,
    selectedEndDate,
    selectedChargerIds,
    setSelectedStartDate,
    setSelectedEndDate,
    setSelectedChargerIds,
  } = usePartnerInsightsData({
    chargerIds: incoming?.preselectedChargerIds,
  });

  const chargerOptions = useMemo(() => {
    const opts = chargers.map(c => ({ label: c.name || 'Unnamed', value: c.uuid as string }));
    return [{ label: 'Select All', value: '__ALL__' }, ...opts];
  }, [chargers]);

  const selectedValues = selectedChargerIds || [];
  const selectedCount = selectedValues.length;

  const formatLocalShort = (iso: string): string => {
    try {
      if (!iso) return '';
      const [y, m, d] = iso.split('-').map((v) => Number(v));
      const dt = new Date(y, (m || 1) - 1, d || 1);
      return dt.toLocaleDateString(undefined, { year: '2-digit', month: '2-digit', day: '2-digit' });
    } catch {
      return iso;
    }
  };

  const lifetimeKwh = Number(data.lifetimeSums?.allKwh || 0);
  const useMWh = lifetimeKwh >= 10000;
  const energyDisplay = useMWh
    ? Math.round(lifetimeKwh / 1000)
    : Math.round(lifetimeKwh);
  const energyUnit = useMWh ? 'MWh' : 'kWh';

  // Check if selected chargers have earnings permissions (meander or split)
  // Only show earnings if at least one selected charger has meander or split permission
  const hasEarningsPermissions = useMemo(() => {
    // If no chargers loaded yet, assume true (will update when chargers load)
    if (chargers.length === 0) return true;
    
    // If specific chargers are selected, check those
    if (selectedValues.length > 0) {
      const selectedChargers = chargers.filter(c => selectedValues.includes(c.uuid as string));
      return selectedChargers.some(charger => {
        const permissions = charger.permissions || [];
        return permissions.includes('meander') || permissions.includes('split');
      });
    }
    
    // If no chargers selected but we have chargers, check if ANY charger has earnings permissions
    // (This handles the case where all chargers are being viewed by default)
    return chargers.some(charger => {
      const permissions = charger.permissions || [];
      return permissions.includes('meander') || permissions.includes('split');
    });
  }, [selectedValues, chargers]);

  const onConfirmDates = (range: DateRange) => {
    if (range.startDate && range.endDate) {
      setSelectedStartDate(range.startDate);
      setSelectedEndDate(range.endDate);
    }
  };

  const handleMultiSelectChange = (vals: string[]) => {
    if (!vals) return;
    if (vals.includes('__ALL__')) {
      // select all chargers (exclude the special option)
      const allIds = chargers.map(c => c.uuid).filter(Boolean) as string[];
      setSelectedChargerIds(allIds);
    } else {
      setSelectedChargerIds(vals);
    }
  };

  const handleRemoveCharger = (chargerIdToRemove: string) => {
    if (!selectedValues || !Array.isArray(selectedValues)) return;
    const updatedIds = selectedValues.filter(id => id !== chargerIdToRemove);
    setSelectedChargerIds(updatedIds.length > 0 ? updatedIds : []);
  };

  const allSelected = chargers.length > 0 && selectedValues.length === chargers.length;

  // Get selected chargers with IDs and names for display
  const selectedChargerData = useMemo(() => {
    if (!selectedValues.length || !Array.isArray(selectedValues)) return [];
    return selectedValues
      .map(id => {
        const charger = chargers.find(c => c?.uuid === id);
        return charger ? { id, name: charger.name || 'Unnamed' } : null;
      })
      .filter((item): item is NonNullable<typeof item> => item != null);
  }, [selectedValues, chargers]);

  const selectedChargerNames = useMemo(() => {
    return selectedChargerData.map(c => c.name);
  }, [selectedChargerData]);

  // Determine display mode: chips for 1-5 chargers, text for 6+
  const useChipsDisplay = selectedChargerNames.length > 0 && selectedChargerNames.length <= 5;

  // Build compact text for 6+ chargers
  const chargerDisplayText = useMemo(() => {
    const count = selectedChargerNames.length;
    if (count === 0 || count <= 5) return null;
    // For 6+, show first 2 and count of remaining
    return `${selectedChargerNames.slice(0, 2).join(', ')}, and ${count - 2} more`;
  }, [selectedChargerNames]);

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: '#1A1D21' }}>
      <ScrollView contentContainerStyle={{ padding: 16 }}>
        <Text style={{ color: '#FFFFFF', fontSize: 18, fontWeight: '600', marginBottom: 12, textAlign: 'center' }}>Insights</Text>

        {/* Filters Row */}
        <View style={{ marginBottom: 10 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, justifyContent: 'space-between' }}>
            <View style={{ flex: 1, paddingRight: 5 }}>
              <Pressable onPress={() => setPickerVisible(true)} style={{ backgroundColor: '#292D33', height: 34, paddingHorizontal: 10, borderRadius: 20, justifyContent: 'center' }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', maxWidth: '100%' }}>
                  <MaterialIcons name="calendar-today" size={16} color="white" style={{ marginRight: 6 }} />
                  <Text numberOfLines={1} ellipsizeMode="tail" style={{ color: 'white', fontSize: 12, flexShrink: 1 }}>{`${formatLocalShort(selectedStartDate)} to ${formatLocalShort(selectedEndDate)}`}</Text>
                </View>
              </Pressable>
            </View>
            <DateRangePicker
              visible={pickerVisible}
              onClose={() => setPickerVisible(false)}
              onConfirm={onConfirmDates}
              initialRange={{ startDate: selectedStartDate, endDate: selectedEndDate }}
            />
            {/* Charger multi-select with compact label */}
            <View style={{ position: 'relative', flex: 1, paddingLeft: 5, paddingRight: 0 }}>
              <MultiSelect
                data={chargerOptions}
                value={allSelected ? (chargers.map(c => c.uuid).filter(Boolean) as string[]) : selectedValues}
                labelField="label"
                valueField="value"
                // search
                placeholder=""
                searchPlaceholder="Search"
                onChange={handleMultiSelectChange}
                style={[dropdownStyles.dropdown, { backgroundColor: '#FFFFFF', borderRadius: 20, height: 34, paddingVertical: 6, paddingHorizontal: 10, width: '100%', borderBottomWidth: 0, borderWidth: 0, borderColor: 'transparent' }]}
                placeholderStyle={[dropdownStyles.placeholderStyle, { fontSize: 12, color: 'transparent' }]}
                selectedTextStyle={[dropdownStyles.selectedTextStyle, { fontSize: 12, color: 'transparent' }]}
                inputSearchStyle={[dropdownStyles.inputSearchStyle, { borderWidth: 1, borderColor: '#636363', borderRadius: 8, paddingHorizontal: 10, color: '#1A1D21' }]}
                renderItem={(item: any, selected?: boolean) => {
                  return (
                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 10, paddingHorizontal: 12 }}>
                      <Text style={{ color: '#1A1D21', fontSize: 14 }}>{item?.label}</Text>
                      <Text style={{ color: selected ? '#51B868' : 'transparent', fontSize: 16, fontWeight: '700' }}>✓</Text>
                    </View>
                  );
                }}
                renderSelectedItem={() => <View style={{ width: 0, height: 0 }} />}
                selectedStyle={{ display: 'none', height: 0, width: 0, padding: 0, margin: 0 }}
                itemContainerStyle={{ borderRadius: 12, overflow: 'hidden', marginHorizontal: 6 }}
                containerStyle={{ position: 'absolute', top: 85, left: 0, right: 0, borderRadius: 20, overflow: 'hidden', zIndex: 1000 }}
                visibleSelectedItem={false}
              />
              <View style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, justifyContent: 'center', alignItems: 'center', pointerEvents: 'none' }}>
                <Text style={{ color: '#1A1D21', fontSize: 12 }}>{`Filter Chargers${selectedCount > 0 ? ` (${selectedCount})` : ''}`}</Text>
              </View>
            </View>
          </View>
          <View style={{ width: '100%', height: 8 }} />
          <View style={{ width: '100%', height: 2 }}>
            <Svg width="100%" height={2}>
              <Line x1="0" y1="1" x2="100%" y2="1" stroke="#636363" strokeWidth={2} strokeDasharray="2 4" strokeLinecap="round" />
            </Svg>
          </View>
        </View>

        {/* No Chargers Selected Message */}
        {selectedChargerNames.length === 0 && (
          <View style={{ marginBottom: 12, paddingHorizontal: 8, paddingVertical: 12, backgroundColor: '#292D33', borderRadius: 12, borderWidth: 1, borderColor: '#636363' }}>
            <Text style={{ color: '#FFA500', fontSize: 13, fontWeight: '600', textAlign: 'center', marginBottom: 4 }}>
              ⚠️ No Chargers Selected
            </Text>
            <Text style={{ color: '#8D8E90', fontSize: 12, textAlign: 'center' }}>
              Please select chargers above to view insights
            </Text>
          </View>
        )}

        {/* Selected Chargers Display - Chips for 1-5, Text for 6+ */}
        {useChipsDisplay && selectedChargerNames.length > 0 && (
          <View style={{ marginBottom: 12, paddingHorizontal: 4 }}>
            <Text style={{ color: '#8D8E90', fontSize: 12, marginBottom: 6, textAlign: 'center' }}>
              {selectedChargerNames.length === 1 ? 'Viewing Charger' : `Viewing ${selectedChargerNames.length} Chargers`}
            </Text>
            <ScrollView 
              horizontal 
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={{ 
                paddingHorizontal: 4,
                justifyContent: selectedChargerNames.length <= 2 ? 'center' : 'flex-start',
                flexGrow: selectedChargerNames.length <= 2 ? 1 : 0,
              }}
            >
              {selectedChargerData.map((chargerInfo, index) => (
                <View 
                  key={`${chargerInfo.id}-${index}`}
                  style={{ 
                    backgroundColor: '#292D33', 
                    paddingVertical: 6, 
                    paddingLeft: 12,
                    paddingRight: 8,
                    borderRadius: 16,
                    marginHorizontal: 4,
                    borderWidth: 1,
                    borderColor: '#3A3F47',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <Text 
                    style={{ 
                      color: '#FFFFFF', 
                      fontSize: 13, 
                      fontWeight: '500',
                      maxWidth: 200,
                    }}
                    numberOfLines={1}
                  >
                    {chargerInfo.name}
                  </Text>
                  <Pressable
                    onPress={() => handleRemoveCharger(chargerInfo.id)}
                    hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    style={({ pressed }) => ({
                      opacity: pressed ? 0.6 : 1,
                      padding: 2,
                    })}
                  >
                    <Text style={{ color: '#8D8E90', fontSize: 16, fontWeight: '600' }}>×</Text>
                  </Pressable>
                </View>
              ))}
            </ScrollView>
          </View>
        )}
        
        {chargerDisplayText && !useChipsDisplay && (
          <View style={{ marginBottom: 12, paddingHorizontal: 4 }}>
            <Text style={{ color: '#8D8E90', fontSize: 12, marginBottom: 2, textAlign: 'center' }}>
              {`Viewing ${selectedChargerNames.length} Chargers`}
            </Text>
            <Text 
              style={{ 
                color: '#FFFFFF', 
                fontSize: 14, 
                fontWeight: '500', 
                textAlign: 'center',
                flexWrap: 'wrap'
              }}
              numberOfLines={2}
            >
              {chargerDisplayText}
            </Text>
          </View>
        )}

        {/* Lifetime Totals Card */}
        <View style={{ backgroundColor: '#292D33', borderRadius: 16, padding: 12, marginBottom: 12 }}>
          {hasEarningsPermissions ? (
            <>
              <View style={{ flexDirection: 'row' }}>
                <View style={{ flex: 1, alignItems: 'center' }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                    <Text style={{ color: '#95F5AA', fontSize: 16, fontWeight: '700' }}>Lifetime Earnings</Text>
                    <TouchableOpacity onPress={() => setLifetimeEarningsTooltipVisible(true)} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                      <Ionicons name="information-circle-outline" size={20} color="#95F5AA" />
                    </TouchableOpacity>
                  </View>
                </View>
                <View style={{ flex: 1, alignItems: 'center' }}>
                  <Text style={{ color: '#61FFF2', fontSize: 16, fontWeight: '700' }}>Lifetime Energy</Text>
                </View>
              </View>
              <View style={{ flexDirection: 'row', marginTop: 6 }}>
                <View style={{ flex: 1, alignItems: 'center' }}>
                  <Text style={{ color: '#95F5AA', fontSize: 26, fontWeight: '500', fontFamily: 'RobotoCondensed-Medium' }}>
                    {new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(data.lifetimeSums?.partnerEarnings || 0))}
                  </Text>
                </View>
                <View style={{ flex: 1, alignItems: 'center' }}>
                  <Text style={{ color: '#61FFF2', fontSize: 26, fontWeight: '500', fontFamily: 'RobotoCondensed-Medium' }}>
                    {new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(energyDisplay)} {energyUnit}
                  </Text>
                </View>
              </View>
            </>
          ) : (
            <>
              <View style={{ alignItems: 'center' }}>
                <Text style={{ color: '#61FFF2', fontSize: 16, fontWeight: '700' }}>Lifetime Energy</Text>
              </View>
              <View style={{ marginTop: 6, alignItems: 'center' }}>
                <Text style={{ color: '#61FFF2', fontSize: 26, fontWeight: '500', fontFamily: 'RobotoCondensed-Medium' }}>
                  {new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(energyDisplay)} {energyUnit}
                </Text>
              </View>
            </>
          )}
        </View>

        {/* Charts */}
        <View style={{ gap: 12 }}>
          {/* Daily Energy Card */}
          <View style={{ backgroundColor: '#292D33', borderRadius: 16, padding: 12 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 0, paddingRight: 8 }}>
              <Text style={{ color: '#61FFF2', fontSize: 16, fontWeight: '700' }}>Daily Energy (kWh)</Text>
              <Text style={{ color: '#61FFF2', fontSize: 11, fontWeight: '600' }}>{`Total: ${data.dailyKwh.reduce((s, d) => s + (Number(d.totalKwh) || 0), 0).toFixed(2)} kWh`}</Text>
            </View>
            <View style={{ backgroundColor: '#292D33', paddingTop: 0, paddingRight: 8, overflow: 'hidden' }}>
              <DailyKwhBarChart data={data.dailyKwh} width={'100%'} />
            </View>
          </View>

          {/* Earnings Card - Only show if user has earnings permissions */}
          {hasEarningsPermissions && (
            <View style={{ backgroundColor: '#292D33', borderRadius: 16, padding: 12 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 0, paddingRight: 8 }}>
                <Text style={{ color: '#95F5AA', fontSize: 16, fontWeight: '700' }}>Accumulated Earnings</Text>
                <Text style={{ color: '#95F5AA', fontSize: 11, fontWeight: '600' }}>{`Total: $${(data.partnerEarnings || []).reduce((s, d) => s + (Number((d as any)?.amount) || 0), 0).toFixed(2)}`}</Text>
              </View>
              <View style={{ backgroundColor: '#292D33', padding: 8, overflow: 'hidden' }}>
                <AccumulatedEarningsLineChart data={(data.partnerEarnings as any) || []} />
              </View>
            </View>
          )}

          <ActiveInactivePieChart 
            active={data.breakdown.activeCount} 
            inactive={data.breakdown.inactiveCount} 
            title="Charger Activity" 
          />
        </View>

        {loading && (
          <Text style={{ color: '#8D8E90', marginTop: 12 }}>Loading...</Text>
        )}
      </ScrollView>

      {/* Lifetime Earnings Tooltip Modal */}
      <Modal
        transparent={true}
        visible={lifetimeEarningsTooltipVisible}
        animationType="fade"
        onRequestClose={() => setLifetimeEarningsTooltipVisible(false)}
      >
        <TouchableOpacity 
          style={{ 
            flex: 1, 
            backgroundColor: 'rgba(0,0,0,0.5)', 
            justifyContent: 'center', 
            alignItems: 'center',
            padding: 20,
          }}
          activeOpacity={1}
          onPress={() => setLifetimeEarningsTooltipVisible(false)}
        >
          <TouchableOpacity 
            activeOpacity={1}
            style={{
              backgroundColor: '#292D33',
              borderRadius: 16,
              padding: 20,
              maxWidth: 400,
              width: '100%',
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 4 },
              shadowOpacity: 0.3,
              shadowRadius: 8,
              elevation: 8,
            }}
            onPress={(e) => e.stopPropagation()}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12, gap: 8 }}>
              <Ionicons name="information-circle" size={24} color="#95F5AA" />
              <Text style={{ color: '#95F5AA', fontSize: 18, fontWeight: '700', flex: 1 }}>
                Lifetime Earnings
              </Text>
              <TouchableOpacity onPress={() => setLifetimeEarningsTooltipVisible(false)}>
                <Ionicons name="close" size={24} color="#8D8E90" />
              </TouchableOpacity>
            </View>
            <Text style={{ color: '#E8E8E9', fontSize: 14, lineHeight: 20 }}>
              This is the total amount paid out over the course of your partnership. Accumulated earnings may include charges that are pending and have not yet been paid to you.
            </Text>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </SafeAreaView>
  );
};

export default PartnerInsights;


